import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, finalize, map, mergeMap, of, shareReplay, take, tap } from 'rxjs';
import { APIResponse } from '../models/apiResponse';
import { AuthService } from '@auth0/auth0-angular';
import { Router } from '@angular/router';
import { OcadoAuth } from '../models/ocado-auth';
import { environment } from '../../../environments/environment';
import { promotionConstants } from '../ocado-constants';

@Injectable({
  providedIn: 'root'
})
export class OcadoAuthService {
  baseUrl: string;
  private _appToken: string = '';
  private _userUid: string = '';

  public get appToken(): string {
    return this._appToken;
  }

  public get userUid(): string {
    return this._userUid;
  }

  public setSessionData(appToken: string, userUid: string): void {
    this._appToken = appToken;
    this._userUid = userUid;
  }

  public authData: BehaviorSubject<OcadoAuth> = new BehaviorSubject({ company_name: '' });
  public isNightlyBatch = new BehaviorSubject(false);
  public isRefreshTokenFetched = new BehaviorSubject(false);
  public roleNameList: string[] = [];
  public roleIdList: string[] = [];
  public companyName!: string;
  public userName!: string;
  public userId!: string;
  public organizationId: string = '0';
  public company_id: string = '0';
  permission: any = [];
  public userList: any[] = [];
  privilege: any[] = [];
  public isStaff: boolean = false;
  public isTokenUnauthorised: boolean = false;

  constructor(private http: HttpClient, private router: Router, public authService: AuthService) {
    this.baseUrl = environment.authBaseUrl;
  }

  fetchMentionUserDetails(entityType: any, entityId: any) {
    const url = this.baseUrl + 'authorised-user/mention-user-details?entity_id=' + entityId + '&entity_type=' + entityType;
    return this.http.get<APIResponse>(url);
  }

  getPermissionDetails(body: any): Observable<boolean> | boolean {
    console.log(this.privilege, body);
    if (this.privilege.find(p => p.screen_id === body.section)) {
      if (!this.permission.find((p: any) => p.screen_id === body.section)) {
        const url = this.baseUrl + 'authorised-user/role-privilege';
        return this.http.post(url, body).pipe(
          tap((data: any) => {
            if (data.result.length) {
              this.permission.push(...data.result);
            }
          }),
          map(data => !!data)
        ).pipe(
          catchError(() => {
            this.router.navigate([ 'unauthorised' ]);
            return of(false);
          })
        );
      } else {
        return true;
      }
    } else {
      if (this.privilege.find(p => p.screen_id === 'summary')) {
        const filterQuery = btoa(JSON.stringify(promotionConstants.defaultFilter));
        this.router.navigate([ '/summary' ], { queryParams: { q: filterQuery, col: this.isStaff ? btoa(JSON.stringify(promotionConstants.buyerColumnsList.map((e: any) => ({ id: e.id, isVisible: e.isVisible })))) : btoa(JSON.stringify(promotionConstants.supplierColumnsList.map((e: any) => ({ id: e.id, isVisible: e.isVisible })))) } });
        return false;
      } else {
        this.router.navigate([ 'unauthorised' ]);
        return false;
      }
    }
  }

  getRoleDetails(body: any): Observable<boolean> {
    const url = this.baseUrl + 'authorised-user/getTokenWithRole';
    return this.http.post(url, body).pipe(
      tap((data: any) => {
        if (data) {
          this.setSessionData(data.result.app_token, data.result.user_uid);
          this.roleIdList = data.result.role_id;
          this.roleNameList = data.result.role_name;
          this.companyName = data.result.company_name;
          this.privilege = data.result.privilege;
          this.userName = data.result.name;
          this.userId = data.result.user_id;
          this.organizationId = data.result.organisation_id;
          this.isStaff = data.result.is_staff;
          this.company_id = data.result.company_id;

          const updatedAuth = {
            company_name: data.result.company_name
          };
          this.authData.next(updatedAuth);
        }
      }),
      map(data => !!data)
    ).pipe(
      catchError(() => {
        this.router.navigate([ 'unauthorised' ]);
        return of(false);
      })
    );
  }

  getRefreshToken(body: any): Observable<boolean> {
    const url = this.baseUrl + 'authorised-user/getRefreshToken';
    return this.http.post(url, body).pipe(
      tap((data: any) => {
        if (data) {
          this.isRefreshTokenFetched.next(false);
          if (data.result?.app_token && data.result?.user_uid) {
            this.setSessionData(data.result.app_token, data.result.user_uid);
          }
          window.location.reload();
        }
      }),
      map(data => !!data)
    ).pipe(
      catchError(() => {
        this.logout();
        return of(false);
      })
    );
  }

  getAccessToken(): Observable<boolean> {
    return this.authService.getAccessTokenSilently().pipe(
      mergeMap((accessToken) => {
        console.log('success-access-data');
        if (accessToken) {
          this.isRefreshTokenFetched.next(false);
        }
        return this.authService.idTokenClaims$.pipe(
          take(1),
          mergeMap((claims) => {
            const idToken = claims?.__raw || '';

            const url1 = this.baseUrl + 'authorised-user/getTokenWithRole';
            const obj = {
              access_token: accessToken || '',
              id_token: idToken,
              scope: 'openid profile email offline_access'
            };
            return this.http.post(url1, obj).pipe(
              tap((data: any) => {
                console.log("success-token-data");
                if (data) {
                  this.setSessionData(data.result.app_token, data.result.user_uid);
                  this.roleIdList = data.result.role_id;
                  this.roleNameList = data.result.role_name;
                  this.companyName = data.result.company_name;
                  this.privilege = data.result.privilege;
                  this.userName = data.result.name;
                  this.userId = data.result.user_id;
                  this.organizationId = data.result.organisation_id;
                  this.isStaff = data.result.is_staff;
                  this.company_id = data.result.company_id;
                  const updatedAuth = {
                    company_name: data.result.company_name
                  };
                  this.authData.next(updatedAuth);
                }
              }),
              map(data => !!data)
            ).pipe(
              catchError(() => {
                console.log("getTokenWithRoleError");
                this.isRefreshTokenFetched.next(true);
                return of(false);
              })
            );
          })
        );
      })
    ).pipe(
      catchError(() => {
        console.log("getAccessTokenError");
        this.isRefreshTokenFetched.next(true);
        this.logout();
        return of(false);
      })
    );
  }

  checkSystemMaintenance(body: any): Observable<boolean> {
    const url = this.baseUrl + 'authorised-user/getTokenWithRole';
    return this.http.post(url, body).pipe(
      map(data => {
        this.router.navigate([ '/' ]);
        return !data;
      })
    ).pipe(
      catchError(() => {
        return of(true);
      })
    );
  }

  getPagePermission(condition: any) {
    // Step 1: filter all matching screen IDs
    const matched = this.permission.filter(condition);

    // Step 2: no match
    if (matched.length === 0) {
      return null;
    }

    // Step 3: single object → return directly
    if (matched.length === 1) {
      return matched[ 0 ];
    }

    // Step 4: merge multiple objects
    const merged: any = {};

    matched.forEach((permission: any) => {
      Object.keys(permission).forEach(key => {
        // Skip these fields
        if (
          [ 'role_id', 'screen_id', 'status' ].includes(key)
        ) {
          return;
        }
        const value = permission[ key ];
        // Handle permission objects dynamically
        // Example:
        // download: { read: true, write: false }
        if (
          value &&
          typeof value === 'object' &&
          'read' in value &&
          'write' in value
        ) {
          // Initialize if not exists
          if (!merged[ key ]) {
            merged[ key ] = {
              read: false,
              write: false
            };
          }
          // TRUE should win
          merged[ key ].read =
            merged[ key ].read || value.read;

          merged[ key ].write =
            merged[ key ].write || value.write;
        }
        else {
          // Non-permission fields
          // keep first value
          if (!(key in merged)) {
            merged[ key ] = value;
          }
        }
      });
    });
    // Optional: put back common fields
    merged.screen_id = condition.screen_id;
    return merged;
  }

  logout() {
    const url = this.baseUrl + 'authorised-user/logout-user';
    const app_token = this._appToken;
    const clearLocalSession = () => {
      this._appToken = '';
      this._userUid = '';
      sessionStorage.clear();
      indexedDB.deleteDatabase(promotionConstants.indexedDBName);
      this.authService.logout({ logoutParams: { returnTo: document.location.origin } });
    };
    if (!app_token) {
      clearLocalSession();
      return of(null as unknown as APIResponse);
    }
    const logout$ = this.http.get<APIResponse>(url, { headers: { Authorization: app_token } }).pipe(
      catchError(() => of(null as unknown as APIResponse)),
      finalize(clearLocalSession),
      shareReplay(1)
    );
    logout$.subscribe();
    return logout$;
  }
}
